import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  LEAD_STATUSES,
  LEAD_STATUS_MAP,
  type BulkLeadAction,
  type BulkResult,
  type LeadStatus,
} from '@goyone/shared';
import { api } from '../../lib/api';
import { useAuth } from '../../auth/AuthProvider';
import { Icon } from '../../components/Icon';
import { Modal } from '../../components/ui';

interface Member {
  id: string;
  name: string;
}

/**
 * شريط الإجراء الجماعي.
 *
 * يظهر حين يُحدَّد عنصر ويختفي حين لا شيء محدَّد. إبقاؤه ظاهرًا دائمًا يجعل
 * أزرار الحذف حاضرة في شاشة لا يقصد أحد الحذف منها.
 */
export function LeadBulkBar({
  ids,
  onClear,
  onDone,
}: {
  ids: string[];
  onClear: () => void;
  /*
   * النتيجة تُسلَّم إلى الأب لا تُعرض من هنا.
   *
   * تنفيذ الإجراء يمسح التحديد، ومسحه يُزيل هذا الشريط من الشجرة — فتضيع
   * معه أي نافذة يملكها. رأيتُ ذلك: الإجراء يقع ولا يظهر تقريره أبدًا.
   */
  onDone: (result: BulkResult) => void;
}) {
  const { atLeast } = useAuth();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<BulkLeadAction | null>(null);

  const members = useQuery({
    queryKey: ['members'],
    queryFn: () => api<Member[]>('/auth/members'),
    enabled: atLeast('MANAGER'),
  });

  const run = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<BulkResult>('/crm/leads/bulk', { method: 'POST', body: { ids, ...body } }),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ['leads'] });
      setPending(null);
      /*
       * التحديد يُمسح دائمًا بعد التنفيذ.
       *
       * إبقاؤه بعد حذف نصف العناصر يترك معرّفات لسجلات لم تعد موجودة،
       * فيُظهر الشريط عددًا لا يقابله شيء.
       */
      onDone(data);
    },
  });

  if (!atLeast('MANAGER')) return null;

  return (
    <>
      <div className="bulkbar">
        <Icon name="check" size={16} />
        <span className="bulkbar-count">حُدِّد {ids.length}</span>
        <button className="btn btn-sm" onClick={onClear}>
          إلغاء التحديد
        </button>

        <div className="bulkbar-actions">
          <button className="btn btn-sm" onClick={() => setPending('STATUS')}>
            تغيير الحالة
          </button>
          <button className="btn btn-sm" onClick={() => setPending('OWNER')}>
            تغيير المسؤول
          </button>
          <button className="btn btn-sm btn-primary" onClick={() => setPending('CONVERT')}>
            تحويل إلى عملاء
          </button>
          <button className="btn btn-sm btn-danger" onClick={() => setPending('DELETE')}>
            <Icon name="trash" size={14} />
            حذف
          </button>
        </div>
      </div>

      {pending && (
        <ActionDialog
          action={pending}
          count={ids.length}
          members={members.data ?? []}
          busy={run.isPending}
          error={run.isError ? run.error : null}
          onClose={() => setPending(null)}
          onConfirm={(body) => run.mutate({ action: pending, ...body })}
        />
      )}
    </>
  );
}

const TITLES: Record<BulkLeadAction, string> = {
  DELETE: 'حذف جماعي',
  STATUS: 'تغيير الحالة',
  OWNER: 'تغيير المسؤول',
  CONVERT: 'تحويل إلى عملاء',
};

function ActionDialog({
  action,
  count,
  members,
  busy,
  error,
  onClose,
  onConfirm,
}: {
  action: BulkLeadAction;
  count: number;
  members: Member[];
  busy: boolean;
  error: unknown;
  onClose: () => void;
  onConfirm: (body: Record<string, unknown>) => void;
}) {
  const [status, setStatus] = useState<LeadStatus>('CONTACTED');
  const [ownerId, setOwnerId] = useState('');
  const [createDeal, setCreateDeal] = useState(false);

  const body =
    action === 'STATUS'
      ? { status }
      : action === 'OWNER'
        ? { ownerId: ownerId || null }
        : action === 'CONVERT'
          ? { createDeal }
          : {};

  const valid = action !== 'OWNER' || ownerId !== '' || ownerId === '';

  return (
    <Modal
      title={TITLES[action]}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className={`btn ${action === 'DELETE' ? 'btn-danger' : 'btn-primary'}`}
            onClick={() => onConfirm(body)}
            disabled={busy || !valid}
          >
            {busy ? 'جارٍ التنفيذ' : `نفّذ على ${count}`}
          </button>
        </>
      }
    >
      {error != null && (
        <div className="alert alert-danger mb-4">
          {error instanceof Error ? error.message : 'تعذّر التنفيذ.'}
        </div>
      )}

      {action === 'DELETE' && (
        <div className="alert alert-danger mb-4">
          <Icon name="alert" size={18} />
          <div>
            سيُحذف {count} عميلًا محتملًا نهائيًا ومعهم أنشطتهم ومواعيدهم. ومن حُوِّل منهم
            إلى حساب لن يُحذف، وسيظهر في تقرير النتيجة.
          </div>
        </div>
      )}

      {action === 'CONVERT' && (
        <>
          <div className="alert alert-info mb-4">
            <Icon name="zap" size={18} />
            <div>
              يُنشَأ حساب لكل عميل محتمل باسمه. ومن حُوِّل من قبل يُتخطّى ويظهر في تقرير
              النتيجة.
            </div>
          </div>

          <label className="row" style={{ cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={createDeal}
              onChange={(e) => setCreateDeal(e.target.checked)}
            />
            <span className="small">أنشئ صفقة مع كل حساب</span>
          </label>
        </>
      )}

      {action === 'STATUS' && (
        <div className="field">
          <label className="label" htmlFor="bulk-status">
            الحالة الجديدة
          </label>
          <select
            id="bulk-status"
            className="select"
            value={status}
            onChange={(e) => setStatus(e.target.value as LeadStatus)}
          >
            {LEAD_STATUSES.filter((s) => s !== 'CONVERTED').map((s) => (
              <option key={s} value={s}>
                {LEAD_STATUS_MAP[s].label}
              </option>
            ))}
          </select>
          <span className="tiny mute">
            «تم التحويل» لا تُضبط يدويًا — تقع بالتحويل نفسه.
          </span>
        </div>
      )}

      {action === 'OWNER' && (
        <div className="field">
          <label className="label" htmlFor="bulk-owner">
            المسؤول الجديد
          </label>
          <select
            id="bulk-owner"
            className="select"
            value={ownerId}
            onChange={(e) => setOwnerId(e.target.value)}
          >
            <option value="">بلا مسؤول</option>
            {members.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        </div>
      )}
    </Modal>
  );
}

/**
 * تقرير النتيجة.
 *
 * يُعرض دائمًا لا عند الإخفاق وحده: «تمّ» بعد خمسين عنصرًا تُخفي اثني عشر
 * لم يقعوا، فيظن المستخدم أنه أنجز ما لم يُنجَز.
 */
export function LeadBulkResult({ result, onClose }: { result: BulkResult; onClose: () => void }) {
  return (
    <Modal
      title="نتيجة الإجراء"
      onClose={onClose}
      footer={
        <button className="btn btn-primary" onClick={onClose}>
          تمام
        </button>
      }
    >
      <div className="stats mb-4">
        <div className="stat">
          <div className="stat-label">نُفِّذ</div>
          <div className="stat-value num" style={{ color: 'var(--success)' }}>
            {result.succeeded}
          </div>
        </div>
        <div className="stat">
          <div className="stat-label">أخفق</div>
          <div
            className="stat-value num"
            style={{ color: result.failed ? 'var(--danger)' : undefined }}
          >
            {result.failed}
          </div>
        </div>
      </div>

      {result.failures.length > 0 && (
        <>
          <h3 className="section-title">ما لم يقع ولماذا</h3>
          <div className="table-wrap" style={{ maxHeight: '18rem', overflowY: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>العميل</th>
                  <th>السبب</th>
                </tr>
              </thead>
              <tbody>
                {result.failures.map((f) => (
                  <tr key={f.id}>
                    <td className="cell-strong">{f.name}</td>
                    <td className="small">{f.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Modal>
  );
}
